import { useState } from "react";
import { Link } from "react-router-dom";
import { OwnerAvatar } from "@/components/owner-avatar";
import { ProviderLogo } from "@/components/provider-logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useK8sClusters } from "@/hooks/use-k8s-clusters";
import { useMachines } from "@/hooks/use-machines";
import type { K8sCluster, Machine } from "@/lib/types";
import { cn } from "@/lib/utils";

function StatusDot({ status }: { status: string }) {
  const colorClass =
    status === "running" ? "bg-emerald-500" : status === "stopped" ? "bg-gray-400" : "bg-amber-400";
  return <span className={cn("inline-block h-2 w-2 rounded-full", colorClass)} />;
}

function MachineRow({ machine }: { machine: Machine }) {
  return (
    <Link
      to={`/machines/${machine.id}`}
      className="flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-accent"
    >
      <ProviderLogo provider={machine.provider} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-mono text-[13px] font-medium text-foreground">
            {machine.name}
          </span>
          <StatusDot status={machine.cloud_status} />
        </div>
        <div className="text-[11px] text-muted-foreground">
          {machine.gpu_count}&times; {machine.gpu_type.toUpperCase()}
          {machine.zone ? ` \u00b7 ${machine.zone}` : ""}
        </div>
      </div>
    </Link>
  );
}

function ClusterRow({ cluster }: { cluster: K8sCluster }) {
  return (
    <Link
      to="/k8s-clusters"
      className="flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-accent"
    >
      <ProviderLogo provider="nebius" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-mono text-[13px] font-medium text-foreground">
            {cluster.name}
          </span>
          <span
            className={cn(
              "inline-block rounded-md px-1.5 py-0.5 text-[10px] font-medium",
              cluster.status.toUpperCase() === "RUNNING"
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                : "bg-muted text-muted-foreground",
            )}
          >
            {cluster.status}
          </span>
        </div>
        <div className="text-[11px] text-muted-foreground">
          {cluster.total_gpus} GPUs &middot; {cluster.total_nodes} nodes
          {cluster.region ? ` \u00b7 ${cluster.region}` : ""}
        </div>
      </div>
    </Link>
  );
}

export function UserResourcesDialog({
  ownerName,
  ownerId,
  isService,
  ownerAvatarUrl,
  children,
}: {
  ownerName: string;
  ownerId: number;
  isService?: boolean;
  ownerAvatarUrl?: string | null;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { data: allMachines = [] } = useMachines(open ? { owner: ownerId.toString() } : undefined);
  const { data: allClusters = [] } = useK8sClusters();

  const machines = allMachines.filter((machine) => machine.cloud_status === "running");
  const clusters = open ? allClusters.filter((cluster) => cluster.allocated_to === ownerId) : [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
        className="cursor-pointer rounded-md transition-colors hover:bg-accent"
      >
        {children}
      </button>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <OwnerAvatar name={ownerName} isService={isService} avatarUrl={ownerAvatarUrl} />
              <span>{ownerName}</span>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-4 px-1">
          {machines.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Machines ({machines.length})
              </div>
              {machines.map((machine) => (
                <MachineRow key={machine.id} machine={machine} />
              ))}
            </div>
          )}
          {clusters.length > 0 && (
            <div className={machines.length > 0 ? "mt-2" : ""}>
              <div className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                K8s Clusters ({clusters.length})
              </div>
              {clusters.map((cluster) => (
                <ClusterRow key={cluster.id} cluster={cluster} />
              ))}
            </div>
          )}
          {machines.length === 0 && clusters.length === 0 && (
            <div className="px-3 py-6 text-center text-[13px] text-muted-foreground">
              No running machines or clusters
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
